import SpoolSelector from "@iewccouk/Components/SpoolSelector";
import ProductQuantityOrdered, {
    ProductQuantityOrderedStylesProps,
} from "@iewccouk/Overrides/Components/ProductQuantityOrdered";
import useCustomQuoteRequired from "@iewccouk/Store/Handlers/Utilities/useCustomQuoteRequired";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: ProductQuantityOrderedStylesProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    // Verndale Customization Start - PUTUP prices styles
    addToCartWrapperPutup?: InjectableCss;
    addToCartButtonPutup?: ButtonPresentationProps;
    quantityOrderedPutup?: ProductQuantityOrderedStylesProps;
    // Verndale Customization End - PUTUP prices styles
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
            background-color: red;

            &.putup-price {
                flex-wrap: wrap;
            }
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
        `,
    },
    quantityOrdered: {
        productQuantityOrderedStyles: {
            cssOverrides: {
                formField: css`
                    inline-size: 100%;
                `,
            },
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({ productContext, productSettings, showPrice, showAddToList }) => {
    // Verndale Customization Start - PUTUP prices
    const [isPutUpPrice, setIsPutUpPrice] = React.useState(false);
    const [numberOfSpools, setNumberOfSpools] = React.useState([1]);
    const [selectedNumberOfSpools, setSelectedNumberOfSpools] = React.useState("1");
    const [spoolsLength, setSpoolsLength] = React.useState(0);
    const [customerSpoolLength, setCustomerSpoolLength] = React.useState(0);
    const [isCmiProductForSpoolSelector, setIsCmiProductForSpoolSelector] = React.useState("");

    useEffect(() => {
        const {
            productInfo,
            product: {
                properties: { allowSpoolOptions, customerLength, isCmiProduct },
                minimumOrderQty,
            },
        } = productContext;

        const isSpoolOptionAllowed =
            allowSpoolOptions === "true" && productInfo.pricing && productInfo.pricing.additionalResults?.spools;

        setIsPutUpPrice(!!isSpoolOptionAllowed);

        if (isSpoolOptionAllowed && productInfo.pricing?.additionalResults?.spools) {
            setNumberOfSpools(getNumberOfSpools(productInfo.pricing?.additionalResults?.spools));
            setSpoolsLength(getSpoolsLength(productInfo.qtyOrdered, selectedNumberOfSpools));
        }
        setIsCmiProductForSpoolSelector(isCmiProduct);
        if(isCmiProduct && isCmiProduct.toLowerCase() === "true" && customerLength) {
            setCustomerSpoolLength(Number(customerLength.replace(/,/g, '')));                   
        }
    }, [productContext]);

    useEffect(() => {
        setSpoolsLength(getSpoolsLength(productContext?.productInfo.qtyOrdered, selectedNumberOfSpools));
    }, [selectedNumberOfSpools]);

    const getNumberOfSpools = (value: string) => {
        return JSON.parse(value);
    };

    const getSpoolsLength = (qty: number, selectedNumberOfSpools: string) => {
        const selectedSpools = parseInt(selectedNumberOfSpools, 10);
        return qty / selectedSpools;
    };

    const isQuoteRequired = useCustomQuoteRequired(productContext);

    // Verndale Customization End - PUTUP prices

    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            <StyledWrapper {...styles.addToCartWrapper}>
                {/* Verndale Customization Start - PUTUP prices */}
                {!isPutUpPrice && (
                    <>
                        <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                        <ProductAddToCartButton
                            data-test-selector={`actionsAddToCart${productContext.product.id}`}
                            extendedStyles={styles.addToCartButton}
                        />
                    </>
                )}

                {isPutUpPrice && (
                    <StyledWrapper
                        {...styles.addToCartWrapperPutup}
                        className={`${isQuoteRequired ? "wrapper-quote" : ""}`}
                    >
                        <ProductQuantityOrdered
                            extendedStyles={styles.quantityOrderedPutup}
                            numberOfSpools={parseInt(selectedNumberOfSpools, 10)} // Verndale Customization - PUTUP prices
                        />
                        {!isQuoteRequired && (
                            <SpoolSelector
                                selectedNumberOfSpools={selectedNumberOfSpools}
                                setSelectedNumberOfSpools={setSelectedNumberOfSpools}
                                numberOfSpools={numberOfSpools}
                                spoolsLength={spoolsLength}
                                uom={productContext.productInfo.unitOfMeasure}
                                qtyOrdered={productContext.productInfo.qtyOrdered}
                                productId={productContext.product.id}
                                customerSpoolLength={customerSpoolLength}
                                isCmiProduct={isCmiProductForSpoolSelector}
                            />
                        )}
                        <ProductAddToCartButton
                            data-test-selector={`actionsAddToCart${productContext.product.id}`}
                            extendedStyles={styles.addToCartButtonPutup}
                        />
                    </StyledWrapper>
                )}
                {/* Verndale Customization End - PUTUP prices */}
            </StyledWrapper>
            {showAddToList && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
